import { Component, componentMixin } from '@resn/gozer-core/component';
import DomComponentMixin from '@resn/gozer-core-mixins/dom-component';
import { html, classMap } from '@resn/gozer-core';
import './index.scss';
import { PAGE_DATA } from 'data/page-data';
import gsap from 'gsap/gsap-core';
import store from 'core/store';
import AnimatedLink from 'component/animated-link';
import svg from 'component/svg';
import Analytics from '@resn/gozer-core/analytics';
import Wipe from 'component/wipe';
import Copy from '@resn/gozer-core/copy';
import { MENU_TIMESCALE } from 'core/constants';

const template = ({ props }) => {
    const classes = { grid: !store().state.mobile };
    return html`
        <div
            class="menuPanel ${classMap({
                visible: props.visible,
            })}"
            data-ui="panel"
        >
            <div class="menuPanel__background" data-ui="background"></div>

            <div class="menuPanel__inner maxWidth ${classMap(classes)}" data-ui="inner">
                <nav class="menuPanel__mainNav" data-ui="mainNav"></nav>

                ${!store().state.mobile ? html`<span class="menuPanel__line" data-ui="line"></span>` : html``}

                <ul class="menuPanel__secondaryNav" data-ui="panel-nav-list"></ul>

                <div class="menuPanel__login  text-heading5" data-ui="login">
                    Login ${svg('avatar-login', 'menuPanel__loginIcon')}
                </div>
            </div>
        </div>
    `;
};

@componentMixin(DomComponentMixin)
class MenuPanel extends Component {
    props = {
        open: false,
        openDelayed: false,

        pathname: () => store().state.route.pathname,
        visible: (props) => props.open || props.openDelayed,
    };

    options = {
        template,
    };

    async onSetup() {
        await this.setupNav();
        await this.setupWipe();
    }

    async setupWipe() {
        this.wipe = await new Wipe().setup({
            options: {
                appendTo: this.el,
            },
            props: {},
        });
    }

    async setupNav() {
        // -- primary
        const pagesPrimary = [
            { ...PAGE_DATA.XS1, postLaunch: true },
            PAGE_DATA.XP1,
            { name: 'Entertainment', path: '/oxefit' },
            { ...PAGE_DATA.SHOP, postLaunch: true },
        ];

        this.navItems1st = await Promise.all(
            pagesPrimary.map(async (page) => {
                return await new NavItem().setup({
                    options: {
                        appendTo: this.ui['mainNav'],
                        hasStylePrimary: true,
                        page,
                        navigateTo: (route) => {
                            if (page.external) window.open(route, '_blank');
                            else this.options.navigateTo(route);
                        },
                    },
                    props: {
                        active: true,
                        open: () => this.props.open,
                        selectedPage: () => page.path === this.props.pathname,
                    },
                });
            }),
        );

        // -- secondary
        const pagesSecondary = [
            PAGE_DATA.HOME,
            PAGE_DATA.INFORMATION,
            PAGE_DATA.LEADERSHIP,
            PAGE_DATA.PRESS,
            PAGE_DATA.INVESTORS,
            PAGE_DATA.CAREERS,
            PAGE_DATA.CONTACT,
            { name: 'support', path: Copy.get('general-menu-support-link'), external: true },
        ];

        this.navItems2nd = await Promise.all(
            pagesSecondary.map(async (page) => {
                return await new NavItem().setup({
                    options: {
                        appendTo: this.ui['panel-nav-list'],
                        page,
                        navigateTo: (route) => {
                            if (page.external) window.open(route, '_blank');
                            else this.options.navigateTo(route);
                        },
                        extraIcon: page.name == 'support' ? 'arrow-rotate' : null,
                    },
                    props: {
                        open: () => this.props.open,
                        selectedPage: () => store().state.route.page == page.name,
                    },
                });
            }),
        );
    }

    onOpen() {
        this.ui.inner.scrollTop = 0;

        store().state.menu.open = true;
        store().state.scrollLock = true;

        const navItems = store().state.mobile
            ? [...this.navItems1st.map((it) => it.el), ...this.navItems2nd.map((it) => it.el), ...[this.ui.login]]
            : [this.navItems1st.map((it) => it.el), this.navItems2nd.map((it) => it.el)];

        if (this.tlOpen) this.tlOpen.kill();
        this.tlOpen = gsap
            .timeline({
                onStart: () => (this.props.openDelayed = true),
                onComplete: () => (store().state.menu.opened = true),
            })
            .set(this.ui.inner, { opacity: 1 }, 0)
            .add(this.wipe.getAnimation(), 0)
            .add(() => (store().state.header.menuOverride = true), 0.5)
            .fromTo(
                this.ui['background'],
                { y: '-100%' },
                {
                    y: 0,
                    duration: 1.6,
                    ease: 'power3.out',
                },
                0,
            )
            .fromTo(
                navItems,
                { y: -(store().state.mobile ? 120 : 80) },
                {
                    y: 0,
                    duration: 1.4,
                    stagger: { each: 0.02, from: 'end' },
                    ease: 'power3.out',
                },
                0.5,
            )
            .fromTo(
                navItems,
                { opacity: 0 },
                {
                    opacity: 1,
                    duration: 1.2,
                    ease: 'sine.out',
                },
                0.7,
            );

        if (this.ui.line)
            this.tlOpen.fromTo(
                this.ui.line,
                { scaleY: 1.5, y: -120, opacity: 0 },
                { scaleY: 1, y: 1, opacity: 1, duration: 2, ease: 'power3.out' },
                0.7,
            );
        this.tlOpen.timeScale(MENU_TIMESCALE);
    }

    offOpen({ firstTrigger }) {
        if (firstTrigger) return;
        store().state.menu.open = false;
        store().state.scrollLock = false;

        const delay = 0.5;

        if (this.tlOpen) this.tlOpen.kill();
        this.tlOpen = gsap
            .timeline({
                onStart: () => (store().state.menu.opened = false),
                onComplete: () => (this.props.openDelayed = false),
            })
            .add(this.wipe.getAnimation(false), 0)
            .add(() => (store().state.header.menuOverride = false), delay)
            .set(this.ui.inner, { opacity: 0 }, delay)
            .set(this.ui.background, { y: '100%' }, delay);

        this.tlOpen.timeScale(MENU_TIMESCALE);
    }
}

export default MenuPanel;

@componentMixin(DomComponentMixin)
class NavItem extends Component {
    props = {
        active: false,
    };

    options = {
        template: ({ options, props }) => {
            return html`<li
                class="${classMap(
                    options.hasStylePrimary
                        ? { menuPanel__mainNavItem: true, isActive: props.active }
                        : { menuPanel__secondaryNavItem: true, isActive: props.active },
                )}"
            ></li>`;
        },
    };

    async onSetup() {
        await new AnimatedLink().setup({
            options: {
                appendTo: this.el,
                label: this.options.page.menuName || this.options.page.name,
                cbClick: (e) => {
                    e.preventDefault();
                    Analytics.trackEvent('Menu', 'click', this.options.page.path.slice(1));
                    this.options.navigateTo(this.options.page.path);
                },
                hasOpenAnimation: false,
                hasStylePrimary: this.options.hasStylePrimary,
                hasExtraIcon: this.options.extraIcon,
            },
            props: {
                active: false,
                open: () => this.props.open,
                selected: (props) => props.selectedOver || this.props.selectedPage,
            },
        });
    }
}
